import { useEffect, useState } from 'react';
import { errorMessage } from '../../lib/errorMessage';
import { Plus, Trash2, X } from 'lucide-react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useToast } from '../../components/Toast';
import { cn } from '../../lib/cn';
import {
  useCatalogSources,
  useSoftDeleteSource,
  useUpsertSource,
  type SourceInput,
  type SourceRow,
} from './catalogSourcesQueries';
import styles from './settings.module.css';

const COLOR_OPTIONS: { label: string; value: string }[] = [
  { label: 'אדום', value: 'red' },
  { label: 'כתום', value: 'orange' },
  { label: 'צהוב', value: 'yellow' },
  { label: 'ירוק', value: 'green' },
  { label: 'כחול', value: 'blue' },
  { label: 'סגול', value: 'purple' },
  { label: 'ורוד', value: 'pink' },
  { label: 'אפור', value: 'gray' },
];

function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[֐-׿]/g, (ch) => `he-${ch.charCodeAt(0).toString(16)}`)
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function CatalogSourcesPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();

  const { data: sources = [], isLoading } = useCatalogSources(orgId);
  const upsertSource = useUpsertSource(orgId, userId);
  const softDeleteSource = useSoftDeleteSource(orgId);

  const [editing, setEditing] = useState<SourceRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState<SourceInput>({
    name: '',
    slug: '',
    utm_source: '',
    color: 'blue',
    sort_order: 0,
  });
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (drawerOpen && !editing) {
      setForm({
        name: '',
        slug: '',
        utm_source: '',
        color: 'blue',
        sort_order: sources.length,
      });
      setSlugTouched(false);
    } else if (drawerOpen && editing) {
      setForm({
        name: editing.name,
        slug: editing.slug,
        utm_source: editing.utm_source ?? '',
        color: editing.color ?? 'blue',
        sort_order: editing.sort_order,
      });
      setSlugTouched(true);
    }
  }, [drawerOpen, editing, sources.length]);

  function openCreate() {
    setEditing(null);
    setDrawerOpen(true);
  }

  function openEdit(row: SourceRow) {
    setEditing(row);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditing(null);
  }

  async function handleSave() {
    if (form.name.trim().length === 0) {
      toast.error('שם הוא שדה חובה');
      return;
    }
    if (form.slug.trim().length === 0) {
      toast.error('slug הוא שדה חובה');
      return;
    }
    try {
      await upsertSource.mutateAsync({ id: editing?.id ?? null, values: form });
      toast.success(editing ? 'המקור עודכן' : 'המקור נוצר');
      closeDrawer();
    } catch (err) {
      const msg = err instanceof Error && err.message.toLowerCase().includes('unique')
        ? 'slug כבר קיים. בחר אחר.'
        : 'לא הצלחנו לשמור · נסה שוב';
      toast.error(msg);
    }
  }

  async function handleDelete(row: SourceRow) {
    if (!window.confirm(`למחוק את "${row.name}"?`)) return;
    try {
      await softDeleteSource.mutateAsync(row.id);
      toast.success('המקור נמחק');
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  return (
    <>
      <div className={styles.pageHead}>
        <h1>מקורות לידים</h1>
        <p className={styles.pageSub}>הערוצים שמהם לידים מגיעים — WhatsApp, אתר, המלצות, פגישות. נשמר על השדה <code>source_id</code> של כל ליד.</p>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3>רשימת מקורות</h3>
          <button type="button" className={styles.btnPrimary} onClick={openCreate}>
            <Plus size={16} aria-hidden="true" />
            מקור חדש
          </button>
        </div>

        {isLoading ? (
          <p className={styles.notice}>טוען...</p>
        ) : sources.length === 0 ? (
          <p className={styles.notice}>אין מקורות עדיין. צור את הראשון בלחיצה על «מקור חדש».</p>
        ) : (
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>שם</th>
                <th>slug</th>
                <th>UTM source</th>
                <th>צבע</th>
                <th>סדר</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr
                  key={s.id}
                  className={styles.dataRow}
                  onClick={() => { openEdit(s); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openEdit(s);
                    }
                  }}
                >
                  <td>{s.name}</td>
                  <td><code dir="ltr">{s.slug}</code></td>
                  <td>{s.utm_source ? <code dir="ltr">{s.utm_source}</code> : <span className={styles.muted}>—</span>}</td>
                  <td>
                    {s.color ? (
                      <span className={styles.colorChip} style={{ background: `var(--color-${s.color}, var(--color-${s.color}-100, #ddd))` }}>
                        {COLOR_OPTIONS.find((c) => c.value === s.color)?.label ?? s.color}
                      </span>
                    ) : <span className={styles.muted}>—</span>}
                  </td>
                  <td>{s.sort_order}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      aria-label="מחק"
                      onClick={(e) => { e.stopPropagation(); void handleDelete(s); }}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawerOpen ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
          onClick={closeDrawer}
        >
          <div
            className={styles.card}
            style={{ maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => { e.stopPropagation(); }}
          >
            <div className={styles.cardHead}>
              <h3>{editing ? 'עריכת מקור' : 'מקור חדש'}</h3>
              <button type="button" className={styles.btnGhost} onClick={closeDrawer} aria-label="סגור">
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className={styles.field}>
              <label htmlFor="source-name">שם</label>
              <input
                id="source-name"
                type="text"
                className={styles.input}
                value={form.name}
                placeholder="WhatsApp · אתר · הפניה · ..."
                onChange={(e) => {
                  const name = e.target.value;
                  setForm((cur) => ({
                    ...cur,
                    name,
                    slug: slugTouched ? cur.slug : slugify(name),
                  }));
                }}
                autoFocus
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="source-slug">slug</label>
              <input
                id="source-slug"
                type="text"
                className={styles.input}
                dir="ltr"
                value={form.slug}
                placeholder="whatsapp"
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm((cur) => ({ ...cur, slug: e.target.value }));
                }}
              />
              <p className={styles.notice}>מזהה ייחודי ל-area. נכנס ל-data-source-id של ה-API ול-query params.</p>
            </div>

            <div className={styles.field}>
              <label htmlFor="source-utm">
                UTM source
                <span className={styles.optional}>אופציונלי</span>
              </label>
              <input
                id="source-utm"
                type="text"
                className={styles.input}
                dir="ltr"
                value={form.utm_source ?? ''}
                placeholder="instagram_story"
                onChange={(e) => { setForm((cur) => ({ ...cur, utm_source: e.target.value })); }}
              />
            </div>

            <div className={styles.field}>
              <label>צבע</label>
              <div className={styles.colorPicker}>
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={cn(styles.colorOption, form.color === c.value && styles.colorOptionActive)}
                    style={{ background: `var(--color-${c.value}, var(--color-${c.value}-100, #ddd))` }}
                    onClick={() => { setForm((cur) => ({ ...cur, color: c.value })); }}
                    aria-label={c.label}
                    title={c.label}
                  />
                ))}
              </div>
            </div>

            <div className={styles.field}>
              <label htmlFor="source-order">סדר תצוגה</label>
              <input
                id="source-order"
                type="number"
                className={styles.input}
                value={form.sort_order}
                onChange={(e) => { setForm((cur) => ({ ...cur, sort_order: Number(e.target.value) })); }}
              />
            </div>

            <div className={styles.actions} style={{ marginBlockStart: 'var(--space-4)' }}>
              <button type="button" className={styles.btnSecondary} onClick={closeDrawer} disabled={upsertSource.isPending}>
                בטל
              </button>
              <button type="button" className={styles.btnPrimary} onClick={() => { void handleSave(); }} disabled={upsertSource.isPending}>
                שמור
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
